// /grow/ 上的任意城市种植日历。
//
// 为什么要有它：/grow/ 底下是 24 座城市的预生成静态页——那是 SEO 面，不是产品能力上限。
// 一个住在苏州或曼彻斯特的人点进 /grow/，看到的是 24 个链接的裸列表，没有自己的城市，
// 也没有任何一句话告诉他"其实哪儿都能算"，于是这一页对他就是死路。
// 这个工具把同一套计算搬到浏览器里跑，任意城市即时出结果，且**不新增一个静态页**
// （几千个近似重复的城市页只会稀释 crawl budget，真正的长尾靠这里兜）。
//
// 算法一份不二：常年值/霜期/播种投影全部来自 js/growcalc.js，评分与画稿直接用游戏本体的
// sim.js / plants.js。所以同一座城市，静态页与这里给出的是同一个答案——
// 这是这个工具敢做的前提，否则两套数字打架比没有工具更糟。
//
// 取数走 /api/wx/archive 代理（不是直连上游）：服务端按 0.05° 取整做缓存键并限流，
// 同一格点第二个人是白拿。近 5 年 4 个变量实测约 66KB / 1 秒。
(function () {
  'use strict';
  const $ = s => document.querySelector(s);
  const EN = (window.SIM_LANG || 'zh') === 'en';
  const T = (zh, en) => EN ? en : zh;
  const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const GC = window.GrowCalc;
  const YEARS = GC.YEARS;

  const MONTHS_ZH = ['', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
  const mname = n => EN ? GC.MON_EN[n] : MONTHS_ZH[n];
  // 月份页的完整英文名（静态页用的是 January 这种全名，这里保持一致）
  const MFULL_EN = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const mfull = n => EN ? MFULL_EN[n] : MONTHS_ZH[n];
  const cropName = c => EN ? ((window.CROPS_EN && CROPS_EN[c.id] && CROPS_EN[c.id].name) || c.id) : c.name;
  const cropHref = c => `${EN ? '/en' : ''}/crops/${c.id}/`;

  let state = null;   // { name, lat, lon, climate, month }
  // 全年基线（每种作物的全年平均分）：切月份要重算渲染，别每次都跑 12×50 遍
  const seasonCache = new WeakMap();
  const seasonOf = climate => {
    let v = seasonCache.get(climate);
    if (!v) { v = GC.seasonBaseline(climate, recommendCrops, CROPS); seasonCache.set(climate, v); }
    return v;
  };

  function setStatus(html, cls) {
    const el = $('#g-status');
    if (el) { el.innerHTML = html || ''; el.className = 's-note' + (cls ? ' ' + cls : ''); }
  }

  // ── 取数 ───────────────────────────────────────────────────────────────────
  async function loadClimate(lat, lon) {
    const p = GC.archiveParams(lat, lon);
    const r = await fetch('/api/wx/archive?' + new URLSearchParams(p));
    if (!r.ok) throw new Error('archive ' + r.status);
    const j = await r.json();
    if (!j.daily || !j.daily.time || !j.daily.time.length) throw new Error('empty');
    return GC.climateFromArchive(j.daily, lat < 0);
  }

  // ── 渲染：当月详情 ─────────────────────────────────────────────────────────
  // 结构与 /grow/<城市>/<月份>/ 静态页刻意保持一致——同一个产品的同一件事，
  // 不该因为"这次是算出来的"就换一副长相。
  function monthBlock(climate, n, cname) {
    const days = GC.monthDays(climate, n);
    if (!days.length) return `<p class="s-note">${T('这个月没有足够的历史数据。', 'Not enough history for this month.')}</p>`;
    const stats = climate.months[n] || {};
    const tmax = days.reduce((s, d) => s + d.tmax, 0) / days.length;
    const tmin = days.reduce((s, d) => s + d.tmin, 0) / days.length;
    const rain = stats.precipSum;
    const third = Math.max(1, Math.floor(days.length / 3));
    const meanT = a => a.reduce((s, d) => s + (d.tmax + d.tmin) / 2, 0) / a.length;
    const drift = meanT(days.slice(-third)) - meanT(days.slice(0, third));
    const sunDays = days.filter(d => d.sun != null);
    const sunH = sunDays.length ? sunDays.reduce((s, d) => s + d.sun, 0) / sunDays.length : null;

    const scored = recommendCrops(CROPS, days);
    const good = GC.pickTop(scored, { season: seasonOf(climate) });   // 限流+旺季优先，见 growcalc 的 pickTop
    const bad = scored.slice(-3).reverse();
    const ordered = good;                     // pickTop 已按 分数→天数 排好
    const uniBand = uniformBand(good);

    // 取数窗口是 YEARS 年，但跨年边界上的月份会落进 YEARS+1 个日历年——夹到 YEARS，
    // 免得出现"6 年里有 2 年"对"近 5 年"的矛盾（与静态页同一处理）
    const fw = Math.min(stats.totalYears || YEARS, YEARS);
    const fy = Math.min(stats.frostYears || 0, fw);
    const frostNote = fw && fy
      ? T(`过去 ${fw} 年里有 ${fy} 年的${mfull(n)}出现过 0°C 及以下的夜晚（最低纪录 ${stats.tminAbs.toFixed(1)}°C），怕冷的作物要准备覆膜。`,
          `In ${fy} of the last ${fw} years, ${cname} saw at least one night at or below 0°C in ${mfull(n)} (record low ${stats.tminAbs.toFixed(1)}°C). Cold-sensitive crops need cover.`)
      : T(`过去 ${YEARS} 年的${mfull(n)}没有出现过 0°C 以下的夜晚——这个月的限制因素不是霜冻。`,
          `No sub-zero nights in ${mfull(n)} in the last ${YEARS} years — frost is not the limiting factor here.`);

    const D = x => GC.fmtMD(x, EN);
    const faStr = climate.frost && climate.frost.firstAutumn ? D(climate.frost.firstAutumn) : null;
    const lsStr = climate.frost && climate.frost.lastSpring ? D(climate.frost.lastSpring) : null;

    const proj = ordered.map(r => ({ r, p: GC.sowProjection(climate, r.crop, n, climate.frost) }));
    const willFinish = proj.filter(x => x.p.matures && x.p.beatsFrost);
    const tooLate = proj.filter(x => x.p.matures && !x.p.beatsFrost);
    // "本地露天根本熟不了"要在**全部作物**里找，不能只看得分高的那几个：
    // 都柏林最有用的一条事实就是露天种不出番茄，而番茄正因为分低进不了 ordered
    const neverHere = CROPS.filter(c => !c.indoor)
      .map(c => ({ crop: c, p: GC.sowProjection(climate, c, n, climate.frost) }))
      .filter(x => !x.p.matures);
    const starts = GC.indoorStarts(CROPS, climate, n);

    const card = r => {
      const c = r.crop;
      const eta = r.estDays ? T(`约 ${r.estDays} 天可收`, `about ${r.estDays} days to harvest`)
                            : T('当前温度下偏慢', 'slow at these temperatures');
      // 与静态页同一套档位与措辞（sim.js 的 SCORE_BANDS）；全场同档时不逐卡标
      const band = scoreBand(r.score);
      const badge = uniBand ? ''
        : `<span class="s-score ${band.cls}" title="${esc(scoreHint())}">${esc(scoreLabel(r.score))}</span>`;
      return `<li class="s-crop">
        <a href="${cropHref(c)}">
          <span class="s-cropart"><svg viewBox="-60 -150 120 165">${plantArt(c, 1, true, false, 100)}</svg></span>
          <b>${c.emoji} ${esc(cropName(c))}</b></a>
        ${badge}
        <span class="s-eta">${esc(eta)}</span>
        <span class="s-why">${esc((r.reasons || [])[0] || '')}</span>
      </li>`;
    };

    return `
  <p class="s-lede">${T(
      `${esc(cname)}的${mfull(n)}，过去 ${YEARS} 年平均在 <b>${tmin.toFixed(1)}~${tmax.toFixed(1)}°C</b>，整月降水约 <b>${rain ? rain.toFixed(0) : '—'} mm</b>。这样的天气，能把什么种到收——下面逐条说。`,
      `Over the last ${YEARS} years, ${mfull(n)} in ${esc(cname)} has run <b>${tmin.toFixed(1)}–${tmax.toFixed(1)}°C</b> with about <b>${rain ? rain.toFixed(0) : '—'} mm</b> of rain across the month. Here is what weather like that will carry to harvest.`)}</p>
  <p class="s-note">❄️ ${esc(frostNote)}</p>
  <p class="s-note">🗓️ ${esc(GC.frostStatusFor(climate.frost, n, EN))} ${esc(GC.frostLine(climate.frost, EN))}</p>
  <p class="s-note">${sunH ? T(`☀️ 本月日均日照约 <b>${sunH.toFixed(1)} 小时</b>。`, `☀️ About <b>${sunH.toFixed(1)} h</b> of sunshine a day this month.`) : ''}
    ${Math.abs(drift) >= 0.8
      ? T(`月内从上旬到下旬${drift < 0 ? '降' : '升'}约 <b>${Math.abs(drift).toFixed(1)}°C</b>${drift < 0 ? '——秋播的窗口正在打开' : ''}。`,
          `Within the month it ${drift < 0 ? 'cools' : 'warms'} by about <b>${Math.abs(drift).toFixed(1)}°C</b> from the first ten days to the last${drift < 0 ? ' — the window for autumn sowings is opening' : ''}.`)
      : T('整月温度基本平稳。', 'Temperatures hold steady through the month.')}</p>

  <h3>${T('这个月种下去，什么时候能收？', 'Sow this month — when would you harvest?')}</h3>
  ${willFinish.length ? `<table class="s-table"><thead><tr>
      <th>${T('作物', 'Crop')}</th><th>${T('天数', 'Days')}</th><th>${T('预计收获', 'Harvest around')}</th></tr></thead>
    <tbody>${willFinish.map(x => `<tr>
      <td><a href="${cropHref(x.r.crop)}">${x.r.crop.emoji} ${esc(cropName(x.r.crop))}</a></td>
      <td>${x.p.days}</td><td>${esc(D(x.p.harvest))}</td></tr>`).join('')}</tbody></table>`
    : `<p>${T('这个月露天下种，没有能顺利收成的作物。', 'Nothing sown this month finishes in the open ground here.')}</p>`}
  ${tooLate.length ? `<p class="s-warn">⏳ ${T(
      `本月已经太晚：<b>${tooLate.map(x => esc(cropName(x.r.crop))).join('、')}</b> 到初霜${faStr ? `（约 ${faStr}）` : ''}时还没长成，会被冻在地里。要么提早几个月种，要么进温室。`,
      `Too late this month: <b>${tooLate.map(x => esc(cropName(x.r.crop))).join(', ')}</b> would still be in the ground when the first frost arrives${faStr ? ` (around ${faStr})` : ''}. Sow them earlier in the year, or grow them under cover.`)}</p>` : ''}
  ${neverHere.length ? `<p class="s-warn">🌡️ ${T(
      `<b>${neverHere.map(x => esc(cropName(x.crop))).join('、')}</b> 在${esc(cname)}露天，任何月份都攒不够成熟所需的积温。本地要种，得进温室或者放在向阳的窗台。`,
      `<b>${neverHere.map(x => esc(cropName(x.crop))).join(', ')}</b> never accumulate enough heat outdoors in ${esc(cname)} — not in any month. Locally these need a greenhouse or a warm windowsill.`)}</p>` : ''}

  ${starts.length ? `<h3>${T('这个月该在室内育苗的', 'Start these indoors this month')}</h3>
  <p class="s-note">${T(
      `${esc(cname)}常年终霜约在 ${lsStr}。下面这些喜温作物直播来不及，要现在播进育苗盘，等终霜过后再移栽出去。`,
      `${esc(cname)}'s average last spring frost is around ${lsStr}. These need a head start under cover — sow into trays now, transplant out after the frost date.`)}</p>
  <ul class="s-avoid">${starts.map(x => `<li><b>${x.crop.emoji} ${esc(cropName(x.crop))}</b> — ${T(
      `终霜前约 ${x.weeks} 周室内播种`, `sow indoors about ${x.weeks} weeks before the last frost`)}</li>`).join('')}</ul>` : ''}

  <h3>${T('这个月最值得种的', 'Best crops to sow now')}</h3>
  ${uniBand ? `<p class="s-note">${esc(uniformBandNote(uniBand))}</p>` : ''}
  ${ordered.length ? `<ol class="s-crops">${ordered.map(card).join('')}</ol>`
    : `<p>${T('这个月本地没有得分理想的露天作物——是难种的月份。窗台盆栽不受室外天气影响，仍然可以种。',
              'No crop scores well in this month here — it is a hard month for open ground. Windowsill pots are unaffected by outdoor weather.')}</p>`}

  <h3>${T('这个月先别种', 'Leave these for another month')}</h3>
  <ul class="s-avoid">${bad.map(r => `<li><b>${r.crop.emoji} ${esc(cropName(r.crop))}</b> — ${esc(GC.limitReason(r, EN))}</li>`).join('')}</ul>`;
  }

  // ── 渲染：整份结果 ─────────────────────────────────────────────────────────
  function render() {
    const { name, climate, month } = state;
    const f = climate.frost || {};
    const D = x => GC.fmtMD(x, EN);
    const hasPair = f.lastSpring && f.firstAutumn && f.freeDays;

    const frostCard = hasPair ? `
  <div class="s-frostcard">
    <div><span class="s-fk">${T('常年终霜', 'Last spring frost')}</span><b>${esc(D(f.lastSpring))}</b></div>
    <div><span class="s-fk">${T('常年初霜', 'First autumn frost')}</span><b>${esc(D(f.firstAutumn))}</b></div>
    <div><span class="s-fk">${T('无霜期', 'Frost-free days')}</span><b>${f.freeDays}${T(' 天', '')}</b></div>
  </div>` : `<p class="s-note">❄️ ${esc(GC.frostLine(f, EN))}</p>`;

    // 12 个月总览：与 /grow/<城市>/ 静态页同一张表，只是月份不再是链接而是切换按钮
    const rows = [];
    for (let n = 1; n <= 12; n++) {
      const days = GC.monthDays(climate, n);
      if (!days.length) continue;
      const tmax = days.reduce((s, d) => s + d.tmax, 0) / days.length;
      const tmin = days.reduce((s, d) => s + d.tmin, 0) / days.length;
      const top = GC.pickTop(recommendCrops(CROPS, days), { limit: 3, perCategory: 1, maxIndoor: 0, season: seasonOf(climate) })
        .map(r => `${r.crop.emoji}${esc(cropName(r.crop))}`).join(' ');
      rows.push(`<tr${n === month ? ' class="on"' : ''}><th><a href="#" data-gmonth="${n}">${mname(n)}</a></th>
        <td>${tmin.toFixed(0)}~${tmax.toFixed(0)}°C</td><td>${top || '—'}</td></tr>`);
    }

    $('#g-result').innerHTML = `
  <h2>${esc(name)}${T(' 种植日历', ' planting calendar')}</h2>
  ${frostCard}
  <ul class="s-months">${Array.from({ length: 12 }, (_, i) => i + 1).map(n => n === month
      ? `<li class="on">${mname(n)}</li>`
      : `<li><a href="#" data-gmonth="${n}">${mname(n)}</a></li>`).join('')}</ul>
  ${monthBlock(climate, month, name)}
  <h3>${T('全年一览', 'The whole year')}</h3>
  <table class="s-table s-cal"><thead><tr><th>${T('月份', 'Month')}</th><th>${T('常年气温', 'Typical')}</th><th>${T('推荐', 'Top picks')}</th></tr></thead>
    <tbody>${rows.join('')}</tbody></table>
  <p class="s-src">${T(
      `气温和降水来自 Open-Meteo 对这个位置的历史观测，按过去 ${YEARS} 年逐日平均。某种作物赶不赶得及长成，是按它自己的起始温度和所需热量算的——这些数字都列在<a href="/crops/">作物图鉴</a>里，想核对随时可以。另外，气象数据抹平了地形：洼地、屋顶和四面无遮挡的菜畦，实际都比这些数字更冷。`,
      `Temperature and rainfall come from Open-Meteo's historical observations for this exact spot, averaged day by day over the last ${YEARS} years. Whether a crop finishes in time is worked out from its own starting temperature and heat requirement — those numbers are listed on the <a href="/en/crops/">crop pages</a> if you want to check them. Weather data is smoothed over terrain: hollows, rooftops and open beds run colder than these figures.`)}</p>
  <p class="s-cta-line"><a class="s-cta" href="${EN ? '/en/' : '/'}">${T('用你窗外的真实天气种一片 →', 'Grow one in your own real weather →')}</a></p>`;
    setStatus('');
    // 结果可分享/可收藏：把位置写进 hash，别人点开直接看到同一份日历
    location.hash = `p=${state.lat.toFixed(3)},${state.lon.toFixed(3)},${encodeURIComponent(name)}`;
  }

  // ── 入口 ───────────────────────────────────────────────────────────────────
  async function run(place, month) {
    setStatus(T('正在取近 5 年的逐日气候…', 'Fetching 5 years of daily climate…'));
    $('#g-result').innerHTML = '';
    try {
      const climate = await loadClimate(place.lat, place.lon);
      state = { name: place.name, lat: place.lat, lon: place.lon, climate,
                month: month || (new Date().getMonth() + 1) };
      render();
      if (window.Track) Track.ev('growtool_city', { m: state.month });
    } catch (e) {
      console.error('[growtool]', e);
      setStatus(T('取数失败了，请稍后再试。', 'Could not fetch the climate data — please try again.'), 's-warn');
    }
  }

  async function search() {
    const q = ($('#g-city') || {}).value;
    if (!q || !q.trim()) return;
    setStatus(T('搜索中…', 'Searching…'));
    try {
      const list = await Weather.searchCity(q.trim());
      if (!list.length) return setStatus(T('没找到这个地方，换个写法试试。', 'No match — try a different spelling.'), 's-warn');
      if (list.length === 1) return run({ lat: list[0].lat, lon: list[0].lon, name: list[0].name });
      $('#g-result').innerHTML = '';
      setStatus(`<div class="f-hits">${list.slice(0, 6).map((c, i) =>
        `<button class="btn btn-sm" data-ghit="${i}">${esc(c.label || c.name)}</button>`).join('')}</div>`);
      window.__ghits = list;
    } catch (e) {
      setStatus(T('搜索失败，请稍后再试。', 'Search failed — please try again.'), 's-warn');
    }
  }

  async function useMyLocation() {
    setStatus(T('定位中…', 'Locating…'));
    try {
      const p = await Weather.locate();
      await run({ lat: p.lat, lon: p.lon, name: p.name });
    } catch (e) {
      setStatus(T('定位失败：', 'Location failed: ') + esc(e.message || ''), 's-warn');
    }
  }

  document.addEventListener('click', e => {
    const hit = e.target.closest('[data-ghit]');
    if (hit) {
      const c = (window.__ghits || [])[+hit.dataset.ghit];
      if (c) run({ lat: c.lat, lon: c.lon, name: c.name });
      return;
    }
    const m = e.target.closest('[data-gmonth]');
    if (m && state) {
      e.preventDefault();
      state.month = +m.dataset.gmonth;
      render();
      const r = $('#g-result'); if (r) r.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    setStatus('');
    const loc = $('#g-locate'); if (loc) loc.addEventListener('click', useMyLocation);
    const btn = $('#g-search'); if (btn) btn.addEventListener('click', search);
    const inp = $('#g-city');
    if (inp) inp.addEventListener('keydown', ev => { if (ev.key === 'Enter') search(); });
    // 带 hash 进来的（别人分享的链接）直接出结果，不用再搜一次
    const m = /p=(-?[\d.]+),(-?[\d.]+),(.+)/.exec(location.hash || '');
    if (m) run({ lat: +m[1], lon: +m[2], name: decodeURIComponent(m[3]) });
  });
})();
